import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { MessageCircle, Send, ShieldAlert, Loader2, FileSignature } from "lucide-react";
import { useI18n } from "../lib/i18n";
import CreateContractModal from "./CreateContractModal";

/**
 * Iter 71 — Chat widget shared by the player + streamer dashboards.
 *
 * Reads `/api/chats/mine`, opens the selected thread from
 * `/api/chats/{id}/messages`, and posts new messages to the same
 * endpoint. Coarse pre-delivery sanitization runs SERVER-side; blocked
 * messages surface the backend's structured `{category, message}` so
 * the sender can rephrase (no client-side censoring — the server is
 * authoritative). Message-based, no realtime: pressing Send round-trips
 * and refreshes the visible thread. Isolation is server-enforced
 * (`_guard_chat_participant_or_admin`).
 */
function isMine(m, me) {
  if (!me || !m) return false;
  return m.senderType === me.role && m.senderId === me.id;
}

function ChatList({ chats, activeId, onSelect, me }) {
  if (!chats.length) {
    return <div className="text-xs text-[#8B97AE]" data-testid="chats-empty">No active chats yet.</div>;
  }
  return (
    <ul className="space-y-1" data-testid="chats-list">
      {chats.map((c) => {
        const label = me?.role === "streamer" ? c.playerNameMasked : c.streamerDisplayName;
        const isActive = c.id === activeId;
        return (
          <li key={c.id}>
            <button
              data-testid={`chat-row-${c.id}`}
              onClick={() => onSelect(c.id)}
              className={`w-full text-left border px-3 py-2 transition-colors ${isActive ? "border-[#2DD4A7] bg-[#2DD4A7]/[0.08]" : "border-white/10 hover:border-white/30"}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-display font-bold text-sm text-[#E8ECF4] truncate">{label || "…"}</span>
                <span className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] shrink-0">{c.vertical}</span>
              </div>
              <div className="text-[10px] font-mono text-[#8B97AE] mt-0.5">
                {c.lastMessageAt ? `last ${c.lastMessageAt.slice(0, 16).replace("T", " ")}` : "no messages yet"}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ChatThread({ chatId, me }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  // Iter 96 — Refs used for polling. `latestAtRef` holds the createdAt of the
  // newest message we've already rendered so the `?since=` incremental fetch
  // never re-serves it. `scrollRef` + `stickToBottomRef` let us append new
  // messages without disrupting scroll position when the user has scrolled up.
  const latestAtRef = useRef("");
  const scrollRef = useRef(null);
  const stickToBottomRef = useRef(true);

  // Iter 99 — PURE updater. Deduplication is computed from `prev` inside the
  // setter (not an external ref) so React 18/19 StrictMode double-invoking
  // this function in dev doesn't swallow all messages. `latestAtRef` is a
  // write-only side-effect on the newest merged row and is idempotent.
  const mergeRows = useCallback((incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    setRows((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const additions = incoming.filter((m) => m && m.id && !existingIds.has(m.id));
      if (additions.length === 0) return prev;
      const merged = [...prev, ...additions];
      // Chronological — server already sorts ascending, but new arrivals
      // may interleave with an in-flight optimistic echo on send. Sort
      // defensively by createdAt then id so ordering is deterministic.
      merged.sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || "") || (a.id || "").localeCompare(b.id || ""));
      const newest = merged[merged.length - 1];
      if (newest?.createdAt) latestAtRef.current = newest.createdAt;
      return merged;
    });
  }, []);

  const loadInitial = useCallback(async () => {
    if (!chatId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/chats/${chatId}/messages`);
      const list = Array.isArray(data) ? data : [];
      latestAtRef.current = "";
      setRows([]);
      mergeRows(list);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  }, [chatId, mergeRows]);

  // Initial load on chat switch.
  useEffect(() => { loadInitial(); }, [loadInitial]);

  // Iter 96 — polling loop while the chat is open. 4 seconds is short enough
  // to feel live for messaging and light enough to not hammer the API. Uses
  // an incremental `?since=` fetch after we have at least one message.
  useEffect(() => {
    if (!chatId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const since = latestAtRef.current;
        const url = since
          ? `/chats/${chatId}/messages?since=${encodeURIComponent(since)}`
          : `/chats/${chatId}/messages`;
        const { data } = await api.get(url);
        if (cancelled) return;
        mergeRows(Array.isArray(data) ? data : []);
      } catch (_) { /* transient — silent */ }
    };
    const iv = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [chatId, mergeRows]);

  // Track whether user is anchored to bottom BEFORE new rows render.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      stickToBottomRef.current = nearBottom;
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [chatId]);

  // Auto-scroll ONLY when the user was already at the bottom. If they've
  // scrolled up to read older messages, incoming rows queue below without
  // yanking the viewport.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [rows]);

  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chats/${chatId}/messages`, { body: trimmed });
      setBody("");
      // Merge the freshly-posted message immediately (dedupe by id) so the
      // sender sees it without waiting for the next poll tick.
      if (data && data.id) mergeRows([data]);
      // Anchor to bottom when the user themselves sent something.
      stickToBottomRef.current = true;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const cat = d && typeof d === "object" ? d.category : null;
      const msg = (d && typeof d === "object" && d.message) || apiErrorMessage(e);
      toast.error(cat ? `${cat.toUpperCase()}: ${msg}` : msg);
    } finally { setSending(false); }
  };

  if (!chatId) {
    return <div className="text-xs text-[#8B97AE] p-6 text-center" data-testid="chat-none-selected">Select a chat to open the thread.</div>;
  }

  return (
    <div className="flex flex-col h-[70vh] max-h-[720px] min-h-[320px]" data-testid={`chat-thread-${chatId}`}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 px-1 py-2">
        {busy && rows.length === 0 && (
          <div className="flex items-center gap-2 text-xs text-[#8B97AE]"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
        )}
        {!busy && rows.length === 0 && (
          <div className="text-xs text-[#8B97AE] text-center py-8" data-testid="chat-thread-empty">No messages yet — say hello.</div>
        )}
        {rows.map((m) => {
          const mine = isMine(m, me);
          const isSystem = m.senderType === "system";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : isSystem ? "justify-center" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
              <div className={`max-w-[75%] px-3 py-2 border ${
                isSystem ? "border-[#F5B841]/40 bg-[#F5B841]/[0.06] text-[#F5B841] text-[11px] font-mono uppercase tracking-widest" :
                mine ? "border-[#2DD4A7]/60 bg-[#2DD4A7]/[0.08] text-[#E8ECF4]" :
                "border-white/15 text-[#E8ECF4]"
              }`}>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] mb-0.5">{isSystem ? "System" : m.senderType} · {(m.createdAt || "").slice(11, 16)}</div>
                <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">{m.body}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-white/10 pt-3 mt-2">
        {me?.role === "streamer" && (
          <div className="mb-2 flex justify-end">
            <button
              data-testid={`chat-create-contract-${chatId}`}
              onClick={() => setContractOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 h-8 border border-[#2DD4A7]/60 text-[#2DD4A7] font-mono text-[10px] uppercase tracking-widest hover:bg-[#2DD4A7]/[0.08] transition-colors"
            ><FileSignature className="w-3 h-3" />Create contract</button>
          </div>
        )}
        <div className="flex items-start gap-2">
          <textarea
            data-testid={`chat-input-${chatId}`}
            rows={2}
            maxLength={2000}
            placeholder="Message…"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 2000))}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="flex-1 rounded-none bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 resize-none"
          />
          <button
            data-testid={`chat-send-${chatId}`}
            onClick={send}
            disabled={sending || !body.trim()}
            className="inline-flex items-center gap-1.5 px-3 h-10 bg-[#2DD4A7] text-[#04150F] font-mono text-[11px] uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40"
          ><Send className="w-3 h-3" />{sending ? "…" : "Send"}</button>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">
          <ShieldAlert className="w-3 h-3" />
          <span>No emails / links / phone numbers · admins can read this chat</span>
        </div>
      </div>
      {contractOpen && (
        <CreateContractModal
          chatId={chatId}
          onClose={() => setContractOpen(false)}
          onCreated={() => { /* pending_payment created — settlement UI will pick it up elsewhere */ }}
        />
      )}
    </div>
  );
}

export default function Chats({ me }) {
  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const { locale, t } = useI18n();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chats/mine");
      const active = (data || []).filter((c) => c.status === "active");
      setChats(active);
      if (active.length > 0 && !activeId) setActiveId(active[0].id);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => ({
    casino: chats.filter((c) => c.vertical === "casino"),
    case_opening: chats.filter((c) => c.vertical === "case_opening"),
  }), [chats]);

  if (loading) return null;
  // Iter 165 — Chats block always renders (even when empty) so the tab
  // never blanks out. The previous `if (chats.length === 0) return null;`
  // guard made the whole tab go dark for new users. Loading state above
  // still short-circuits so we never flash "no chats yet" before data
  // arrives. Role-aware bilingual empty copy — streamer / player each
  // get a nudge that matches how they'd START a chat on this platform.
  const isStreamer = me?.role === "streamer";
  const emptyCopy = isStreamer
    ? (locale === "de"
        ? "Noch keine Chats. Schalte einen Spieler im Marktplatz frei, um ein Gespräch zu beginnen."
        : locale === "es"
        ? "Aún no hay chats. Desbloquee un jugador en el marketplace para iniciar una conversación."
        : "No chats yet. Unlock a player from the marketplace to start a conversation.")
    : (locale === "de"
        ? "Noch keine Chats. Sobald ein Streamer den Kontakt mit dir freischaltet, erscheint hier eure Unterhaltung."
        : locale === "es"
        ? "Aún no hay chats. En cuanto un streamer desbloquee el contacto con usted, su conversación aparecerá aquí."
        : "No chats yet. When a streamer unlocks contact with you, your conversation will appear here.");

  return (
    <section className="lg:col-span-12 border border-white/15 p-6 lg:p-8" data-testid="chats-tile">
      <div className="flex items-center gap-2 mb-1"><MessageCircle className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">Chats</h2></div>
      <p className="text-xs text-[#8B97AE] mb-4">{t("chats.desc")}</p>
      {chats.length === 0 ? (
        <div
          className="border border-white/8 border-dashed p-6 text-center text-xs text-[#8B97AE]"
          data-testid="chats-empty-state"
        >
          {emptyCopy}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
          <div className="border border-white/8 p-3 max-h-[480px] overflow-y-auto">
            {["casino", "case_opening"].map((v) => (
              grouped[v].length > 0 && (
                <div key={v} className="mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-1">{v}</div>
                  <ChatList chats={grouped[v]} activeId={activeId} onSelect={setActiveId} me={me} />
                </div>
              )
            ))}
          </div>
          <div className="border border-white/8 p-3">
            <ChatThread chatId={activeId} me={me} />
          </div>
        </div>
      )}
    </section>
  );
}
